/**
 * MCP 管理 —— 自建服务器接入地址 + 小智官方语音接入 + 工具清单 + 调用日志。
 *
 * 两张核心卡片：
 * 1) 自建服务器 MCP 接入地址：复制 / 轮换 token（旧地址立即失效）；
 * 2) 小智官方语音：粘贴「MCP 接入点」wss → 启用后平台后台反向连上小智，
 *    展示「连接于 … · 语音已调用 N 次」，调用次数随 SSE 事件实时增长。
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { VoiceStatus } from '@znjj/shared';
import { mcpApi, mcpKeys, type McpCallLog } from '../api/mcp';
import { Icon } from '../components/Icon';
import { EmptyState, fmt, toast, useCopy } from '../components/ui';
import { useStream } from '../hooks/useStream';

const VOICE_STATUS: Record<VoiceStatus, { text: string; tag: string; dot: string }> = {
  connected: { text: '已连接', tag: 'tag--on', dot: 'dot--live' },
  connecting: { text: '连接中…', tag: 'tag--warn', dot: 'dot--warn' },
  disconnected: { text: '未连接', tag: 'tag--off', dot: 'dot--off' },
  error: { text: '连接异常', tag: 'tag--warn', dot: 'dot--warn' },
};

/** 调用参数摘要：只显示一层键值，避免日志行过长 */
function argsPreview(args: unknown): string {
  if (!args || typeof args !== 'object') return '无参数';
  const text = Object.entries(args as Record<string, unknown>)
    .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
    .join('，');
  return text || '无参数';
}

function LogRow({ log }: { log: McpCallLog }) {
  const fromVoice = log.source === 'xiaozhi';
  return (
    <div className="list__row">
      <span className="list__icon">
        <Icon name={fromVoice ? 'wifi' : 'plug'} size={16} />
      </span>
      <span className="list__main">
        <span className="list__title">
          <span className="mono">{log.toolName}</span>
          <span className="tag tag--plain t-xs dim" style={{ marginLeft: 6 }}>
            {fromVoice ? '语音调用' : '接入地址'}
          </span>
        </span>
        <span className="list__sub">{argsPreview(log.args)}</span>
      </span>
      <span className="stack" style={{ gap: 2, alignItems: 'flex-end' }}>
        <span className={'tag ' + (log.ok ? 'tag--on' : 'tag--off')}>{log.ok ? '成功' : '失败'}</span>
        <span className="t-xs dim mono">{fmt.hhmm(log.createdAt)}</span>
      </span>
    </div>
  );
}

export function McpPage() {
  const qc = useQueryClient();
  const copy = useCopy();
  useStream(true);

  const settingsQuery = useQuery({ queryKey: mcpKeys.settings, queryFn: mcpApi.settings });
  const toolsQuery = useQuery({ queryKey: mcpKeys.tools, queryFn: mcpApi.tools, staleTime: Infinity });
  const logsQuery = useQuery({ queryKey: mcpKeys.logs, queryFn: () => mcpApi.logs(50) });
  const voiceQuery = useQuery({ queryKey: mcpKeys.voice, queryFn: mcpApi.voiceBinding });

  const settings = settingsQuery.data?.settings;
  const binding = voiceQuery.data?.binding;
  const tools = toolsQuery.data?.list ?? [];
  const logs = logsQuery.data?.list ?? [];

  const [urlInput, setUrlInput] = useState('');
  useEffect(() => {
    setUrlInput(binding?.url ?? '');
  }, [binding?.url]);

  const rotate = useMutation({
    mutationFn: mcpApi.rotate,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mcpKeys.settings });
      toast.ok('接入地址已轮换，旧地址立即失效');
    },
    onError: (err: Error) => toast.err(err.message),
  });

  const connectVoice = useMutation({
    mutationFn: () => mcpApi.connectVoice(urlInput.trim()),
    onSuccess: (res) => {
      qc.setQueryData(mcpKeys.voice, res);
      toast.ok('已开始连接小智接入点');
    },
    onError: (err: Error) => toast.err(err.message),
  });

  const disconnectVoice = useMutation({
    mutationFn: mcpApi.disconnectVoice,
    onSuccess: (res) => {
      qc.setQueryData(mcpKeys.voice, res);
      toast.ok('已断开与小智的连接');
    },
    onError: (err: Error) => toast.err(err.message),
  });

  const status = VOICE_STATUS[(binding?.status ?? 'disconnected') as VoiceStatus];
  const isLive = binding?.status === 'connected' || binding?.status === 'connecting';

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">MCP 管理</h1>
        <p className="page-sub">
          用 MCP 协议把小智等语音智能体接到你的设备上：自建接入地址走 HTTP，小智官方语音走反向 WebSocket。
        </p>
      </div>

      {/* 卡片 1：自建服务器 MCP 接入地址 */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card__head">
          <div className="card__title">
            <Icon name="plug" size={16} />
            自建服务器 MCP 接入地址
          </div>
          <button
            className="btn btn--sm btn--ghost"
            type="button"
            disabled={rotate.isPending}
            onClick={() => {
              if (confirm('轮换后旧地址立即失效，需要重新填入小智 / NAS。确认轮换？')) rotate.mutate();
            }}
          >
            <Icon name="refresh-cw" size={14} />
            轮换
          </button>
        </div>

        {settingsQuery.isPending ? (
          <div className="skeleton" style={{ height: 38 }} />
        ) : settings ? (
          <>
            <div className="codebox">
              <span className="codebox__text">{settings.endpointUrl}</span>
              <button
                className="btn btn--icon"
                type="button"
                aria-label="复制接入地址"
                onClick={() => void copy(settings.endpointUrl)}
              >
                <Icon name="copy" size={14} />
              </button>
            </div>
            <p className="card__desc" style={{ marginTop: 8 }}>
              把这行地址填进小智 / NAS 的 <span className="mono">.mcp_server_settings.json</span>，
              平台即按 MCP 协议提供 <span className="mono">tools/call</span>；令牌与登录态完全分离，
              只作用于你名下的设备。
            </p>
          </>
        ) : null}
      </div>

      {/* 卡片 2：小智官方语音接入 */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card__head">
          <div className="card__title">
            <Icon name="wifi" size={16} />
            小智官方语音接入
          </div>
          <span className={'tag ' + status.tag}>
            <i className={'dot ' + status.dot} />
            {status.text}
          </span>
        </div>

        <div className="field">
          <span className="field__label">小智「MCP 接入点」地址</span>
          <input
            className="input"
            value={urlInput}
            placeholder="wss://api.xiaozhi.me/mcp/?token=ey..."
            onChange={(e) => setUrlInput(e.target.value)}
          />
          <span className="field__hint">
            在 xiaozhi.me 智能体页复制接入点地址粘贴到此处。启用后由平台主动反向连接小智，
            无需公网暴露你的设备。
          </span>
        </div>

        <div className="btn-row" style={{ marginTop: 12 }}>
          <button
            className="btn btn--primary"
            type="button"
            disabled={!urlInput.trim() || connectVoice.isPending}
            onClick={() => connectVoice.mutate()}
          >
            <Icon name="plug" size={15} />
            {connectVoice.isPending ? '正在连接…' : isLive ? '重新连接' : '启用'}
          </button>
          <button
            className="btn btn--ghost"
            type="button"
            disabled={!isLive || disconnectVoice.isPending}
            onClick={() => disconnectVoice.mutate()}
          >
            <Icon name="x" size={15} />
            断开
          </button>
        </div>

        <div className="row row--wrap t-sm muted" style={{ marginTop: 12, gap: 10 }}>
          <span>
            连接于{' '}
            <span className="mono">
              {binding?.connectedAt ? fmt.dateTime(binding.connectedAt) : '—'}
            </span>
          </span>
          <span>
            语音已调用 <span className="mono" style={{ color: 'var(--color-primary)' }}>{binding?.callCount ?? 0}</span> 次
          </span>
        </div>

        {binding?.lastError ? (
          <div className="row" style={{ marginTop: 10, alignItems: 'flex-start' }}>
            <Icon name="alert-triangle" size={14} style={{ color: 'var(--color-warning)', marginTop: 3 }} />
            <span className="t-sm" style={{ color: 'var(--color-warning)', lineHeight: 1.6 }}>
              {binding.lastError}
            </span>
          </div>
        ) : null}
      </div>

      {/* 卡片 3：工具清单 */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card__head">
          <div className="card__title">
            <Icon name="cpu" size={16} />
            可被语音调用的工具
          </div>
          <span className="tag tag--plain t-xs dim">{tools.length} 个</span>
        </div>
        {toolsQuery.isPending ? (
          <div className="skeleton" style={{ height: 60 }} />
        ) : (
          <div className="stack" style={{ gap: 8 }}>
            {tools.map((t) => (
              <div key={t.name} className="row" style={{ alignItems: 'flex-start' }}>
                <Icon name="chevron-right" size={14} style={{ color: 'var(--color-primary)', marginTop: 3 }} />
                <span className="stack" style={{ gap: 2, minWidth: 0 }}>
                  <span className="mono" style={{ fontSize: 12.5 }}>{t.name}</span>
                  <span className="t-sm muted" style={{ lineHeight: 1.6 }}>{t.description}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 卡片 4：调用日志 */}
      <div className="card card--flat" style={{ marginBottom: 24 }}>
        <div className="card__head">
          <div className="card__title">
            <Icon name="terminal" size={16} />
            调用日志
          </div>
          <button
            className="btn btn--sm btn--ghost"
            type="button"
            disabled={logsQuery.isFetching}
            onClick={() => void logsQuery.refetch()}
          >
            <Icon name="refresh-cw" size={14} />
            刷新
          </button>
        </div>

        {logsQuery.isPending ? (
          <div className="skeleton" style={{ height: 80 }} />
        ) : logs.length ? (
          <div className="list">
            {logs.map((log) => (
              <LogRow key={log.id} log={log} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon="terminal"
            title="暂无调用记录"
            desc="用自建接入地址发起一次 tools/call，或让小智语音控制设备后，这里会实时出现日志。"
          />
        )}
      </div>
    </>
  );
}
